/**
 * Les QUATRE refus qui parlent d'un DOSSIER, et non d'une pièce.
 *
 * Module frère de `ged-owned-by-tender`, qui reste le point d'entrée du sujet et
 * ré-exporte ce qui est ici : les appelants importent depuis lui. Coupé le
 * 2026-09-18, quand le refus de la DESTINATION a trouvé son consommateur — le
 * hub dépassait les 300 lignes du dépôt.
 *
 * 🔴 **F3, 2026-09-19 (soir) — la QUATRIÈME est PARTIE, un jour après être
 * arrivée.** `phraseDuRefusDUnDossierSupprime` lisait un refus que la décision PO
 * Q1 a supprimé côté serveur le soir même : `DELETE /v1/ged/folders/{id}` n'émet
 * plus `GED_FOLDER_OWNED_BY_TENDER` (re-mesuré : `refus_du_dossier_supprime` a
 * zéro occurrence dans `backend/src`), et ses deux clés ont été retirées des
 * DEUX catalogues. La branche restait chaînée dans `erreurDunDossierDuCoffre` et
 * faisait rougir `i18n-keys-exist` sur deux clés que plus rien ne traduit.
 * ⚠️ **Le registre n'annonce plus son nombre** : il l'a déjà payé deux fois ici
 * — « deux refus » puis « ces trois phrases », puis « elles sont QUATRE » la
 * veille de la troisième mort. Ce qui tient, c'est le CRITÈRE : une phrase vit
 * ici si son sujet est un dossier.
 *
 * Ce que ces phrases ont en commun : leur sujet est un dossier. Servies par la
 * phrase de la pièce, elles diraient « Ce document fait partie… » à quelqu'un
 * qui supprime un dossier, ou qui y range un document qui, lui, est libre.
 *
 * 🔴 **F7 / F8, 2026-09-19 (soir) — trois refus du coffre n'atteignaient AUCUN
 * écran traduit**, et ils parlent tous d'un dossier : ils vivent donc ici, avec
 * ceux de la portée « appel d'offres ». Mesuré par `grep` sur `frontend/src` :
 * `SYSTEM_FOLDER_PROTECTED` → 2 occurrences, toutes deux dans des COMMENTAIRES ;
 * `PARENT_NOT_FOUND` et le `QUOTA_EXCEEDED` de PROFONDEUR → 0. Les quatre portes
 * du dossier système (dépôt, déplacement de masse, PATCH, DELETE) rendaient donc
 * la phrase FRANÇAISE en dur du serveur, y compris dans l'interface anglaise, et
 * la création de dossier rendait ses deux refus en ANGLAIS à un francophone.
 *
 * ⚠️ **Deux d'entre elles portent le MÊME code** (`GED_FOLDER_OWNED_BY_TENDER`,
 * mesuré le 2026-09-19 dans `TenderOwner.refus_du_dossier_cible` et
 * `…refus_du_dossier_deplace`) et ne se distinguent donc PAS à la lecture de
 * l'erreur : c'est la PORTE qui sait laquelle servir. Ranger un document dans un
 * dossier de consultation (PATCH d'un fichier, dépôt, déplacement de masse) → la
 * phrase de la CIBLE ; déplacer le dossier lui-même (PATCH d'un dossier) → celle
 * du DÉPLACEMENT. Une fonction qui « devinerait » en lisant le message serveur se
 * casserait au premier mot changé côté serveur.
 *
 * ⚠️ Les clés sont écrites EN ENTIER, DANS l'appel à `t` : une clé construite
 * échapperait à tout extracteur, et une clé absente des deux catalogues laisse la
 * parité verte (mesure du 2026-09-17, reprise dans le hub).
 */

import type { TFunction } from 'i18next'

export const GED_FILE_OWNED_BY_TENDER = 'GED_FILE_OWNED_BY_TENDER'
/** Le refus d'une DESTINATION : le dossier visé appartient à une consultation.
 *  Posé côté serveur le 2026-09-18 (`TenderOwner.refus_du_dossier_cible`), levé
 *  par le dépôt et par le rangement des quatre onglets ET par le PATCH du coffre
 *  (`_ged_files_helpers._garder_la_portee_du_patch`). Code PROPRE : ici le
 *  document est libre, c'est la cible qui est possédée. */
export const GED_FOLDER_OWNED_BY_TENDER = 'GED_FOLDER_OWNED_BY_TENDER'

/** `t` rend `undefined` tant qu'i18next n'est pas initialisé (mesuré sur la
 *  23.16.8) : une chaîne vide ou absente ne doit jamais devenir le toast. */
function texte(valeur: unknown): string | null {
    return typeof valeur === 'string' && valeur.trim() ? valeur : null
}

/** Défauts français du refus d'un DOSSIER supprimé : [nombre inconnu, une pièce, plusieurs]. */
const DOSSIER_AVEC_REFERENCE = [
    "Ce dossier contient des pièces du dossier de l'appel d'offres {{reference}} : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
    "Ce dossier contient {{count}} pièce du dossier de l'appel d'offres {{reference}} : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
    "Ce dossier contient {{count}} pièces du dossier de l'appel d'offres {{reference}} : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
] as const
const DOSSIER_SANS_REFERENCE = [
    "Ce dossier contient des pièces du dossier d'un appel d'offres : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
    "Ce dossier contient {{count}} pièce du dossier d'un appel d'offres : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
    "Ce dossier contient {{count}} pièces du dossier d'un appel d'offres : on ne peut pas le supprimer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
] as const

/**
 * Le refus d'un DOSSIER dont la suppression emporterait des pièces d'AO, ou `null`.
 *
 * Posé côté serveur le 2026-09-17 (`TenderOwner.refus_du_dossier`, lu par les
 * cinq portes qui suppriment un dossier) : MÊME code que la pièce, plus
 * `files_count`.
 *
 * Sans `files_count`, la phrase dit « des pièces » : un nombre inventé serait
 * pire qu'aucun. Le cas reste atteignable — un serveur plus ancien, une porte
 * qui ne joint pas le compte — mais plus depuis `readError`, qui transporte les
 * clés extra depuis le 2026-09-18.
 */
export function phraseDuRefusDUnDossier(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as {
        code?: unknown
        details?: { tender_reference?: unknown; files_count?: unknown }
    }
    if (code !== GED_FILE_OWNED_BY_TENDER) return null
    const nombre = details?.files_count
    const count = typeof nombre === 'number' && nombre > 0 ? nombre : undefined
    const rang = count === undefined ? 0 : count === 1 ? 1 : 2
    const reference = details?.tender_reference
    if (typeof reference === 'string' && reference.trim()) {
        return texte(
            t('sales.tenders.documents.folderOwnedByTender', {
                defaultValue: DOSSIER_AVEC_REFERENCE[rang],
                reference,
                ...(count === undefined ? {} : { count }),
            }),
        )
    }
    return texte(
        t('sales.tenders.documents.folderOwnedByTenderNoReference', {
            defaultValue: DOSSIER_SANS_REFERENCE[rang],
            ...(count === undefined ? {} : { count }),
        }),
    )
}

/**
 * Le refus d'une DESTINATION qui appartient à une consultation, ou `null`.
 *
 * 🔴 **Les deux clés dormaient sans consommateur** (`targetFolderOwnedByTender`,
 * `…NoReference`, posées dans les deux catalogues le 2026-09-18) : le serveur
 * levait ce 409 depuis le même jour — au rangement et au dépôt des quatre
 * onglets (`borner_le_dossier_cible`) comme au PATCH du coffre
 * (`_garder_la_portee_du_patch`) — et l'écran montrait la phrase FRANÇAISE du
 * serveur mot pour mot, donc en français dans l'interface anglaise. C'est le
 * défaut que ce module entier a été écrit pour fermer, sur la seule porte qui
 * n'avait pas encore sa lecture.
 *
 * ⚠️ Pas de geste à interpoler : cette phrase-là ne parle que du rangement. Le
 * dépôt lève le MÊME code (« Déposez-le depuis la fiche… » couvre les deux) —
 * une seconde rédaction par porte divergerait au premier changement de contrat.
 */
export function phraseDuRefusDUnDossierCible(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as { code?: unknown; details?: { tender_reference?: unknown } }
    if (code !== GED_FOLDER_OWNED_BY_TENDER) return null
    const reference = details?.tender_reference
    if (typeof reference === 'string' && reference.trim()) {
        return texte(
            t('sales.tenders.documents.targetFolderOwnedByTender', {
                defaultValue:
                    "Ce dossier fait partie du dossier de l'appel d'offres {{reference}} : on ne peut pas y ranger un document depuis cette fiche. Déposez-le depuis la fiche de l'appel d'offres, onglet Documents.",
                reference,
            }),
        )
    }
    return texte(
        t('sales.tenders.documents.targetFolderOwnedByTenderNoReference', {
            defaultValue:
                "Ce dossier fait partie du dossier d'un appel d'offres : on ne peut pas y ranger un document depuis cette fiche. Déposez-le depuis la fiche de l'appel d'offres, onglet Documents.",
        }),
    )
}

/**
 * Le refus d'un DOSSIER de consultation qu'on DÉPLACE depuis le coffre, ou `null`.
 *
 * 🔴 **B3, mesuré le 2026-09-19.** `updateFolder` (`ged-folders.ts`) levait
 * `new Error('Failed to update folder: ' + statut)` : depuis que le PATCH porte
 * la garde de portée (`api/ged_folders.py` l.208, geste « déplacer »,
 * C11 du 2026-09-18), le coffre affichait donc **un statut HTTP** là où le
 * serveur envoie le code, la consultation ET le `request_id` d'une vraie panne.
 * La clé `folderMovedOwnedByTender` avait été posée dans les deux catalogues le
 * 2026-09-18 et n'avait **aucun consommateur** : le remède était écrit quinze
 * lignes plus bas, sur `deleteFolder`.
 *
 * ⚠️ « Déplacer », jamais « modifier » : D1 rouvre le RENOMMAGE partout, et le
 * serveur ne garde que la branche `parent_id` (`if body.parent_id is not
 * _UNSET`). Un dossier renommé reste dans l'arbre de sa consultation.
 */
export function phraseDuRefusDUnDossierDeplace(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as { code?: unknown; details?: { tender_reference?: unknown } }
    if (code !== GED_FOLDER_OWNED_BY_TENDER) return null
    const reference = details?.tender_reference
    if (typeof reference === 'string' && reference.trim()) {
        return texte(
            t('sales.tenders.documents.folderMovedOwnedByTender', {
                defaultValue:
                    "Ce dossier fait partie du dossier de l'appel d'offres {{reference}} : on ne peut pas le déplacer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
                reference,
            }),
        )
    }
    // Sans référence, « de l'appel d'offres  : » serait une phrase trouée. Le cas
    // est atteignable : `refus_du_dossier_deplace` rend `tender_reference` tel
    // qu'il est en base, et `tenders.auto_reference` est nullable.
    return texte(
        t('sales.tenders.documents.folderMovedOwnedByTenderNoReference', {
            defaultValue:
                "Ce dossier fait partie du dossier d'un appel d'offres : on ne peut pas le déplacer ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
        }),
    )
}

// ─── LES REFUS PROPRES DU COFFRE ────────────────────────────────────

/** 422 — dossier SYSTÈME (« Documents rattachés ») : `_raise_system_folder_protected`. */
export const SYSTEM_FOLDER_PROTECTED = 'SYSTEM_FOLDER_PROTECTED'
/** 404 — parent absent, ou hors de l'entité courante (anti-énumération). */
export const PARENT_NOT_FOUND = 'PARENT_NOT_FOUND'
/** 403 — `QUOTA_EXCEEDED` de PROFONDEUR. La `reason` est ce qui le distingue. */
const RAISON_PROFONDEUR = 'depth_limit_exceeded'

/**
 * Le refus d'un dossier GÉRÉ PAR ARGALIS, ou `null`.
 *
 * 🔴 **F7, mesuré le 2026-09-19 (soir).** Quatre portes du coffre lèvent ce 422
 * — le dépôt, le déplacement de masse, le PATCH et le DELETE d'un dossier — et
 * `_raise_system_folder_protected` (`api/ged_helpers.py`) y met une phrase
 * FRANÇAISE en dur. Aucun écran ne la lisait : `toastApiError` l'affichait mot
 * pour mot, donc en français dans l'interface anglaise, sur les quatre.
 *
 * ⚠️ La phrase couvre les deux familles de geste, parce que le CODE ne les
 * distingue pas : modifier / supprimer le dossier lui-même, et s'en servir comme
 * DESTINATION. Une rédaction par porte divergerait au premier changement de
 * contrat — c'est la leçon des trois phrases d'AO ci-dessus.
 *
 * ## 🔴 H9, 2026-09-20 : la phrase taisait la QUATRIÈME porte, et H7 l'a changée
 *
 * Elle énumérait quatre interdits (renommer, déplacer, supprimer, déposer) et
 * passait sous silence le cinquième émetteur du 422 : **créer un sous-dossier**
 * (`api/ged_folders.create_folder`). Quelqu'un qui venait d'être refusé sur ce
 * geste-là lisait une phrase qui ne parlait pas de lui.
 *
 * ⚠️⚠️ **Et la nommer sèchement l'aurait rendue FAUSSE le jour même.** La
 * décision PO H7 du 2026-09-20 OUVRE ce geste sur les dossiers d'appel d'offres :
 * un dossier `is_system` porteur de `tender_id` accepte désormais un
 * sous-dossier, tout en refusant toujours les quatre autres gestes — il sert
 * donc encore cette phrase. Écrire « ni y créer un sous-dossier » aurait menti à
 * chaque refus de renommage d'un dossier de consultation. La seconde phrase dit
 * donc l'exception, et elle est vraie des deux côtés : sur « Documents
 * rattachés » elle explique le refus qu'on vient de lire, sur un dossier d'AO
 * elle rappelle le seul geste qui, lui, passe.
 *
 * ⚠️ « Documents rattachés » reste fermé pour une raison qui lui est propre :
 * ses enfants n'existent pas dans `ged_folders`, ils sont DÉRIVÉS du `metadata`
 * des fichiers. Un sous-dossier physique y serait créé, rendu 201, et affiché
 * par aucun écran.
 */
export function phraseDunDossierSysteme(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    if ((erreur as { code?: unknown }).code !== SYSTEM_FOLDER_PROTECTED) return null
    return texte(
        t('ged.refus.system_folder_protected', {
            defaultValue:
                "Ce dossier est géré par Argalis : son contenu s'y range tout seul, et on ne peut ni le renommer, ni le déplacer, ni le supprimer, ni y déposer un document à la main. Seuls les dossiers d'un appel d'offres acceptent qu'on y crée un sous-dossier.",
        }),
    )
}

/**
 * Les deux refus de la CRÉATION d'un dossier du coffre, ou `null`.
 *
 * 🔴 **F8, mesuré le 2026-09-19 (soir).** `POST /v1/ged/folders` rend 404
 * `PARENT_NOT_FOUND` (« Parent folder not found ») et 403 `QUOTA_EXCEEDED`
 * (« Folder depth limit exceeded for your plan »), les deux en ANGLAIS en dur, et
 * `createFolder` les jetait tels quels : l'utilisateur francophone du coffre
 * lisait l'anglais, sans savoir quoi faire.
 *
 * ⚠️⚠️ **Ce `QUOTA_EXCEEDED`-là n'est PAS un refus de taille.** C'est un plafond
 * d'ARBORESCENCE (`check_quota(additional_depth=…)`, 403), et non le poids d'un
 * fichier ou l'espace restant (413 `DOCUMENT_FILE_TOO_LARGE` /
 * `DOCUMENT_STORAGE_QUOTA_EXCEEDED`). Les confondre enverrait « libérez de la
 * place » à quelqu'un dont l'arbre est trop profond — un geste qui ne débloque
 * rien. La garde n'est donc pas le seul code : c'est la `reason`
 * (`depth_limit_exceeded`, `ged_repository/quota.py` l.122), la seule valeur que
 * les deux plafonds ne partagent pas.
 */
export function phraseDeCreationDunDossier(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as { code?: unknown; details?: { reason?: unknown } }
    if (code === PARENT_NOT_FOUND) {
        return texte(
            t('ged.refus.parent_not_found', {
                defaultValue:
                    "Le dossier parent n'existe plus, ou il n'appartient pas à cet établissement : rafraîchissez l'arborescence et recommencez.",
            }),
        )
    }
    if (code === 'QUOTA_EXCEEDED' && details?.reason === RAISON_PROFONDEUR) {
        return texte(
            t('ged.refus.folder_depth_limit', {
                defaultValue:
                    "Votre formule limite la profondeur de l'arborescence : ce sous-dossier serait trop bas. Créez-le plus haut dans l'arbre, ou changez de formule.",
            }),
        )
    }
    return null
}
